import { useEffect, useMemo, useState } from "react";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Download, Filter, Calendar, Building2, FileSpreadsheet, TrendingUp } from "lucide-react";

/**
 * Purchase Reports & Filtering — admin analytics on all vendor bills.
 * Filters: by month, custom date range, by vendor, or vendor + date range.
 * Report views: KPIs · Top vendors · HSN-wise · Tax-slab wise · Monthly trend
 * Underlying data: /reports/purchases/list + /reports/purchases/summary
 */
export default function PurchaseReportsPage() {
    const [mode, setMode] = useState("month");         // month | range
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [vendors, setVendors] = useState([]);
    const [vendorId, setVendorId] = useState("");
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
    }, []);

    const params = useMemo(() => {
        const p = {};
        if (mode === "month" && month) p.month = month;
        else if (mode === "range") { if (start) p.start = start; if (end) p.end = end; }
        if (vendorId) p.vendor_id = vendorId;
        return p;
    }, [mode, month, start, end, vendorId]);

    const runReport = async () => {
        try {
            const [l, s] = await Promise.all([
                api.get("/reports/purchases/list", { params }),
                api.get("/reports/purchases/summary", { params }),
            ]);
            setRows(l.data);
            setSummary(s.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to load report"); }
    };

    useEffect(() => { runReport(); /* eslint-disable-next-line */ }, [params]);

    const exportCsv = () => {
        const header = ["Bill Date", "Bill #", "Vendor", "GSTIN", "Subtotal", "CGST", "SGST", "IGST", "Total Tax", "Grand Total", "Items"];
        const csv = [header, ...rows.map(r => [
            r.bill_date || "",
            r.bill_number || "",
            r.vendor?.name || "",
            r.vendor?.gstin || "",
            r.subtotal || 0, r.cgst || 0, r.sgst || 0, r.igst || 0,
            r.total_tax || 0, r.grand_total || 0,
            (r.line_items || []).length,
        ])].map(row => row.map(v => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        }).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hdb-purchases-${mode === "month" ? month : `${start}_to_${end}`}${vendorId ? "-vendor" : ""}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="purchase-reports-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Purchase Reports</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Vendor spend analytics · GST tracking · monthly trends
                    </p>
                </div>
                <button onClick={exportCsv} disabled={rows.length === 0}
                        title={rows.length === 0 ? "No purchases in this filter to export" : "Download CSV"}
                        className="hdb-btn hdb-btn-ghost" data-testid="pur-export-csv">
                    <Download size={14}/> Export CSV
                </button>
            </div>

            {/* Filter bar */}
            <div className="hdb-card p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <Filter size={14} className="text-slate-500"/>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Filters</span>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
                        <button onClick={() => setMode("month")} data-testid="pur-mode-month"
                                className={`text-xs font-bold px-3 py-1.5 rounded ${mode === "month" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
                            <Calendar size={11} className="inline mr-1"/> By month
                        </button>
                        <button onClick={() => setMode("range")} data-testid="pur-mode-range"
                                className={`text-xs font-bold px-3 py-1.5 rounded ${mode === "range" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
                            <Calendar size={11} className="inline mr-1"/> Custom range
                        </button>
                    </div>

                    {mode === "month" ? (
                        <label className="block">
                            <div className="hdb-label">Month</div>
                            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                                   className="hdb-input hdb-mono" data-testid="pur-month" />
                        </label>
                    ) : (
                        <>
                            <label className="block">
                                <div className="hdb-label">Start</div>
                                <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                                       className="hdb-input hdb-mono" data-testid="pur-start" />
                            </label>
                            <label className="block">
                                <div className="hdb-label">End</div>
                                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                                       className="hdb-input hdb-mono" data-testid="pur-end" />
                            </label>
                        </>
                    )}

                    <label className="block flex-1 min-w-[200px]">
                        <div className="hdb-label">Vendor</div>
                        <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                                className="hdb-input" data-testid="pur-vendor">
                            <option value="">All vendors</option>
                            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                    </label>
                </div>
            </div>

            {/* KPI cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pur-kpis">
                    <Kpi label="Bills" value={summary.kpis.bills || 0} />
                    <Kpi label="Total spend" value={fmt(summary.kpis.spend || 0)} accent="slate" />
                    <Kpi label="GST paid" value={fmt(summary.kpis.tax || 0)} accent="amber" />
                    <Kpi label="Avg / bill" value={fmt((summary.kpis.spend || 0) / Math.max(1, summary.kpis.bills || 1))} accent="mint" />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {summary?.top_vendors?.length > 0 && (
                    <div className="hdb-card p-5" data-testid="pur-top-vendors">
                        <div className="flex items-center gap-2 mb-3">
                            <Building2 size={14} className="text-slate-500"/>
                            <h3 className="hdb-heading font-bold text-slate-900">Top vendors ({summary.top_vendors.length})</h3>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="text-slate-400 text-xs uppercase tracking-wider">
                                <tr><th className="text-left">Vendor</th><th className="text-right">Bills</th><th className="text-right">Spend</th><th className="text-right">Tax</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {summary.top_vendors.map((v, i) => (
                                    <tr key={i}>
                                        <td className="py-1.5">{v.vendor_name || "(unknown)"}<div className="text-[10px] font-mono text-slate-400">{v.vendor_gstin}</div></td>
                                        <td className="py-1.5 text-right font-mono">{v.bills}</td>
                                        <td className="py-1.5 text-right font-mono font-bold">{fmt(v.spend)}</td>
                                        <td className="py-1.5 text-right font-mono text-slate-500">{fmt(v.tax)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {summary?.tax_slabs?.length > 0 && (
                    <div className="hdb-card p-5" data-testid="pur-tax-slabs">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp size={14} className="text-slate-500"/>
                            <h3 className="hdb-heading font-bold text-slate-900">GST slab breakdown</h3>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="text-slate-400 text-xs uppercase tracking-wider">
                                <tr><th className="text-left">Slab</th><th className="text-right">Taxable value</th><th className="text-right">Tax</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {summary.tax_slabs.map((s, i) => (
                                    <tr key={i}>
                                        <td className="py-1.5 font-mono">{s.tax_percent || 0}%</td>
                                        <td className="py-1.5 text-right font-mono">{fmt(s.value)}</td>
                                        <td className="py-1.5 text-right font-mono font-bold">{fmt(s.tax)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {summary?.hsn_wise?.length > 0 && (
                    <div className="hdb-card p-5 lg:col-span-2" data-testid="pur-hsn-wise">
                        <div className="flex items-center gap-2 mb-3">
                            <FileSpreadsheet size={14} className="text-slate-500"/>
                            <h3 className="hdb-heading font-bold text-slate-900">HSN-wise spend (top 20)</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-slate-400 text-xs uppercase tracking-wider">
                                    <tr><th className="text-left">HSN</th><th className="text-right">GST %</th><th className="text-right">Qty</th><th className="text-right">Value</th><th className="text-right">Tax</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {summary.hsn_wise.slice(0, 20).map((h, i) => (
                                        <tr key={i}>
                                            <td className="py-1.5 font-mono">{h.hsn || "—"}</td>
                                            <td className="py-1.5 text-right font-mono text-slate-500">{h.tax_percent || 0}%</td>
                                            <td className="py-1.5 text-right font-mono">{h.qty_total}</td>
                                            <td className="py-1.5 text-right font-mono">{fmt(h.value)}</td>
                                            <td className="py-1.5 text-right font-mono">{fmt(h.tax)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail rows */}
            <div className="hdb-card mt-6 overflow-hidden" data-testid="pur-detail-rows">
                <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                    <BarChart3 size={14} className="text-slate-500"/>
                    <h3 className="hdb-heading font-bold text-slate-900">All purchases in this period ({rows.length})</h3>
                </div>
                {rows.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">
                        No purchases match this filter.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-2">Date</th>
                                <th className="text-left px-4 py-2">Bill #</th>
                                <th className="text-left px-4 py-2">Vendor</th>
                                <th className="text-right px-4 py-2">Items</th>
                                <th className="text-right px-4 py-2">Subtotal</th>
                                <th className="text-right px-4 py-2">Tax</th>
                                <th className="text-right px-4 py-2">Grand Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                                <tr key={i} data-testid={`pur-row-${i}`} className="hover:bg-slate-50/50">
                                    <td className="px-4 py-1.5 text-slate-600 font-mono text-xs">{r.bill_date}</td>
                                    <td className="px-4 py-1.5 font-mono text-xs">{r.bill_number || "—"}</td>
                                    <td className="px-4 py-1.5">{r.vendor?.name || "—"}<div className="text-[10px] font-mono text-slate-400">{r.vendor?.gstin}</div></td>
                                    <td className="px-4 py-1.5 text-right font-mono">{(r.line_items || []).length}</td>
                                    <td className="px-4 py-1.5 text-right font-mono text-xs">{fmt(r.subtotal)}</td>
                                    <td className="px-4 py-1.5 text-right font-mono text-xs text-slate-500">{fmt(r.total_tax)}</td>
                                    <td className="px-4 py-1.5 text-right font-mono font-bold">{fmt(r.grand_total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value, accent = "slate" }) {
    const accentCls = {
        slate: "bg-slate-900 text-white",
        mint:  "bg-gradient-to-br from-emerald-600 to-emerald-500 text-white",
        amber: "bg-gradient-to-br from-amber-500 to-amber-400 text-white",
    }[accent] || "bg-slate-900 text-white";
    return (
        <div className="hdb-card p-4 flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${accentCls}`}>
                <BarChart3 size={16}/>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
                <div className="text-lg font-black text-slate-900 truncate hdb-mono">{value}</div>
            </div>
        </div>
    );
}
